import { getAppAccess } from "@/api";
import { patchAccessPermission } from "@/utils/accessPermissionPatch";
import { syncUserEmployeeInStorage } from "@components/utils/authEmployeeContext";
import { isInAccountReadOnlySession } from "@components/utils/impersonationSession";

export { isYourStuffAllowed } from "@/utils/isYourStuffAllowed";

export const SYNC_ACCESS_PERMISSION_EVENT = "syncAccessPermission";

export function requestAccessPermissionSync(vueRoot) {
  vueRoot?.$emit(SYNC_ACCESS_PERMISSION_EVENT);
}

function parseStoredJson(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
}

export async function refreshAccessPermissionFromApi({
  accountId,
  userId,
  user = null,
  patchUserAccessEnabled = false,
}) {
  if (!accountId || !userId) {
    return null;
  }
  // ER-10317: Preview token is the admin user — never overwrite synthetic grant.
  if (isInAccountReadOnlySession()) {
    return null;
  }

  try {
    const response = await getAppAccess(accountId, userId);
    if (!response?.data) {
      return null;
    }

    const userForPatch = user ?? parseStoredJson("user");
    const refreshed = patchAccessPermission({
      accessPermission: response.data,
      accountId,
      user: userForPatch,
      isEnabled: patchUserAccessEnabled,
    });

    localStorage.setItem("accessPermission", JSON.stringify(refreshed));
    syncUserEmployeeInStorage({
      user: userForPatch,
      accessPermission: refreshed,
      accountId,
    });
    return refreshed;
  } catch (error) {
    console.error("Error refreshing access permission:", error);
    return null;
  }
}

/**
 * Re-fetches access for the logged-in user when the affected user matches.
 */
export async function refreshAccessPermissionIfCurrentUser({
  accountId,
  affectedUserId = null,
  affectedEmail = null,
  patchUserAccessEnabled = false,
  emit = null,
}) {
  const accessPermission = parseStoredJson("accessPermission");
  const currentUser = accessPermission?.user;
  if (!currentUser?.id) {
    return null;
  }

  const matchesById = affectedUserId != null && String(currentUser.id) === String(affectedUserId);
  const matchesByEmail =
    affectedEmail != null &&
    currentUser.email?.toLowerCase() === String(affectedEmail).toLowerCase();

  if (!matchesById && !matchesByEmail) {
    return null;
  }

  const refreshed = await refreshAccessPermissionFromApi({
    accountId,
    userId: currentUser.id,
    patchUserAccessEnabled,
  });

  if (refreshed && emit) {
    emit(refreshed);
  }

  return refreshed;
}
